'use client'

import { createContext, useContext, useState, useCallback, ReactNode } from 'react'

// Tier definitions matching the plan
export type AuthTier = 'free' | 'collector' | 'gallery' | 'enterprise' | 'individual' | 'institutional'

export interface User {
  id: string
  email: string
  name: string
  avatar?: string
  tier: AuthTier
  watchlist: {
    artists: number
    lots: number
  }
  alerts: {
    used: number
    limit: number
  }
}

// Tier limits and features
const tierLimits = {
  free: {
    watchlistArtists: 3,
    alertsPerMonth: 1,
    historyDays: 90,
    historyYears: 0.25,
    canExport: false,
    aiInsights: 'basic',
  },
  collector: {
    watchlistArtists: Infinity,
    alertsPerMonth: 10,
    historyDays: 1825, // 5 years
    historyYears: 5,
    canExport: false,
    aiInsights: 'standard',
  },
  gallery: {
    watchlistArtists: Infinity,
    alertsPerMonth: Infinity,
    historyDays: 10950, // 30 years
    historyYears: 30,
    canExport: true,
    aiInsights: 'full',
  },
  enterprise: {
    watchlistArtists: Infinity,
    alertsPerMonth: Infinity,
    historyDays: 10950, // 30 years
    historyYears: 30,
    canExport: true,
    aiInsights: 'full',
  },
  individual: {
    watchlistArtists: Infinity,
    alertsPerMonth: Infinity,
    historyDays: 18250, // 50 years
    historyYears: 50,
    canExport: true,
    aiInsights: 'full',
  },
  institutional: {
    watchlistArtists: Infinity,
    alertsPerMonth: Infinity,
    historyDays: 18250, // 50 years
    historyYears: 50,
    canExport: true,
    aiInsights: 'full',
  },
} as const

// Tier pricing
export const tierPricing = {
  free: { price: 0, period: '' },
  collector: { price: 79, period: '/mo' },
  gallery: { price: 249, period: '/mo' },
  enterprise: { price: 799, period: '/mo' },
  individual: { price: 199, period: '/mo' },
  institutional: { price: 0, period: 'Custom' },
} as const

// AI feature tiers
type AIFeatureLevel = 'basic' | 'standard' | 'full'
const aiFeatureAccess: Record<string, AIFeatureLevel> = {
  'price-prediction': 'standard',
  'market-analysis': 'basic',
  'artist-comparison': 'standard',
  'portfolio-optimization': 'full',
  'risk-assessment': 'full',
  'trend-forecasting': 'standard',
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string) => Promise<void>
  logout: () => void
  // Feature access helpers
  canAccessHistory: (years: number) => boolean
  canExport: boolean
  canUseAI: (feature: string) => boolean
  alertsRemaining: number
  watchlistLimit: number
  tierLabel: string
  // Upgrade helpers
  upgradeRequired: (feature: string) => AuthTier | null
  getTierForFeature: (feature: string) => AuthTier
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Mock user for demo purposes
const mockUser: User = {
  id: 'demo-user-1',
  email: 'demo@salle.art',
  name: 'Demo User',
  avatar: undefined,
  tier: 'free',
  watchlist: {
    artists: 2,
    lots: 5,
  },
  alerts: {
    used: 0,
    limit: 1,
  },
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(mockUser) // Start with mock user for demo
  const [isLoading, setIsLoading] = useState(false)

  const isAuthenticated = user !== null

  const login = useCallback(async (email: string) => {
    setIsLoading(true)
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))
    setUser({
      ...mockUser,
      email,
    })
    setIsLoading(false)
  }, [])

  const logout = useCallback(() => {
    setUser(null)
  }, [])

  // Feature access helpers
  const canAccessHistory = useCallback((years: number): boolean => {
    if (!user) return false
    const limits = tierLimits[user.tier]
    return years <= limits.historyYears
  }, [user])

  const canExport = user ? tierLimits[user.tier].canExport : false

  const canUseAI = useCallback((feature: string): boolean => {
    if (!user) return false
    const requiredLevel = aiFeatureAccess[feature] || 'basic'
    const userLevel = tierLimits[user.tier].aiInsights

    const levels: AIFeatureLevel[] = ['basic', 'standard', 'full']
    return levels.indexOf(userLevel as AIFeatureLevel) >= levels.indexOf(requiredLevel)
  }, [user])

  const alertsRemaining = user
    ? Math.max(0, tierLimits[user.tier].alertsPerMonth - user.alerts.used)
    : 0

  const watchlistLimit = user
    ? tierLimits[user.tier].watchlistArtists
    : 0

  const tierLabel = user?.tier
    ? user.tier.charAt(0).toUpperCase() + user.tier.slice(1)
    : 'Guest'

  // Determine which tier is needed for a feature
  const getTierForFeature = useCallback((feature: string): AuthTier => {
    switch (feature) {
      case 'unlimited-watchlist':
      case '5-year-history':
      case 'standard-ai':
        return 'collector'
      case 'export':
      case '30-year-history':
      case 'unlimited-alerts':
      case 'full-ai':
        return 'gallery'
      case 'team-seats':
      case 'white-label':
      case 'custom-integrations':
        return 'enterprise'
      default:
        return 'free'
    }
  }, [])

  // Check if upgrade is required for a feature
  const upgradeRequired = useCallback((feature: string): AuthTier | null => {
    if (!user) return 'free'

    const requiredTier = getTierForFeature(feature)
    const tiers: AuthTier[] = ['free', 'collector', 'gallery', 'enterprise']

    if (tiers.indexOf(user.tier) >= tiers.indexOf(requiredTier)) {
      return null // User has access
    }
    return requiredTier
  }, [user, getTierForFeature])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        canAccessHistory,
        canExport,
        canUseAI,
        alertsRemaining,
        watchlistLimit,
        tierLabel,
        upgradeRequired,
        getTierForFeature,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
